import { Hotkey, HotkeysService } from "angular2-hotkeys";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormControl, ValidationErrors } from "@angular/forms";
import { Observable, first, firstValueFrom, map, of, switchMap, takeUntil } from "rxjs";
import { AccountService, ISharedOkrsAccountSetting } from "@gtmhub/accounts/accounts-service";
import { IAssigneesStoreState } from "@gtmhub/assignees/redux/assignee-reducer";
import dayjs from "@gtmhub/dayjs";
import { IIndicator } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { OKRsEventType } from "@gtmhub/okrs/events";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { TeamsActions } from "@gtmhub/teams";
import { IReduxTeam } from "@gtmhub/teams/models/models";
import { ITeamsStoreState } from "@gtmhub/teams/redux/teams-reducer";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { IdMap } from "@gtmhub/util/utils";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { NewComment } from "@webapp/comments/models/comments.models";
import { CommentsFacade } from "@webapp/comments/services/comments-facade.service";
import { ConfidenceScale, ConfidenceType } from "@webapp/configuration/models/configuration.model";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { localize } from "@webapp/localization/utils/localization.utils";
import { Goal } from "@webapp/okrs/goals/models/goal.models";
import { ConfidenceSettingsService } from "@webapp/okrs/metrics/services/confidence-settings.service";
import { MetricPipe } from "@webapp/okrs/pipes/metric.pipe";
import { getDisplayableOwnerIds } from "@webapp/okrs/utils/utils";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { ISessionSettings, Session, UserAction } from "@webapp/sessions/models/sessions.model";
import { FormOf } from "@webapp/shared/form/models/form.models";
import { NoteWithGifAndMentions } from "@webapp/shared/rich-text-editor-with-mentions/rich-text-editor-with-mentions.component";
import { shouldDisableMaxMinDate } from "@webapp/ui/date-picker/utils/date-picker.utils";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";
import { CacheFormOnAccidentalModalClose, CacheableFormGroup } from "../../../../shared/utils/accidental-modal-close";
import {
  dueDateIsInTheSessionTimePeriod,
  generateRemoveAllMentionsAriaLabel,
  getMetricStatusInfoAriaLabel,
  getOwnerIds,
  noDueDate,
  noDueDateOrHardDateIsInTheFuture,
  sessionIsArchivedOrInTheFutureOrOkrIsAbandoned,
} from "../../../utils/metric.utils";
import { ICheckin, IConfidence, Metric } from "../../models/metric.models";
import { MetricsFacade } from "../../services/metrics-facade.service";

const defaultMetricConfidence: IConfidence = {
  value: 0.7 /** confidence values send/received from api should always be float */,
  reason: "",
};
const cacheTimeout = 1000 * 60 * 5; // 5 minutes

interface UpdateMetricData {
  confidence: number;
  newValue: number;
  note: NoteWithGifAndMentions;
  dateOfUpdate: Date;
  customFields: CfMap;
}

type UpdateMetricForm = FormOf<UpdateMetricData>;

export type UpdateMetricModalData = {
  metricId: string;
};

@Component({
  selector: "update-metric",
  templateUrl: "./update-metric.component.html",
  styleUrls: ["./update-metric.component.less"],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateMetricComponent implements OnInit, OnDestroy {
  private planningSession: Session;
  private currentDate: string;
  private isCurrentUserAdmin: boolean;
  private userId: string;
  private goal: IGoal;
  private sessions: Session[];
  private assignees: Record<string, Assignee>;
  private teams: IdMap<IReduxTeam>;
  private metricId: string;
  private maxDateHotkey: Hotkey | Hotkey[];

  public validationStates: {
    isDataValid?: boolean;
    noSessionAlert?: boolean;
    hardDeadlineOutsideSession?: boolean;
    deadlineAlert?: boolean;
    archivedMetricAlert?: boolean;
    krPastHardDeadlineAlert?: boolean;
    showNumericValueField?: boolean;
    abandonedObjectiveAlert?: boolean;
    sessionStartsOnAlert?: boolean;
    showRadioOptions?: boolean;
  } = {};
  public datepickerOptions: {
    minDate: Date;
    maxDate: Date;
  };
  public metric: Metric;
  public statusAriaLabel: string;
  public statusInfoAriaLabel: string;
  public removeAllMentionsAriaLabel: string;
  public loading = true;
  public indicators: {
    saving?: IIndicator;
  } = {};
  public confidenceType: ConfidenceType;
  public confidenceScale: ConfidenceScale;
  @CacheFormOnAccidentalModalClose({ timeout: cacheTimeout })
  public updateMetricForm: CacheableFormGroup<UpdateMetricForm>;
  public customFieldsLength = 0;
  public shouldDisableUpdateDateBound: (date: Date) => boolean;
  public filterBySessionIdBound: (customFields: ICustomField[]) => ICustomField[];
  public hasPermissionManageGoals: boolean;
  public ownerIds: string[];

  constructor(
    private currentUserService: CurrentUserService,
    private confidenceSettingsService: ConfidenceSettingsService,
    private formBuilder: FormBuilder,
    private metricsFacade: MetricsFacade,
    private broadcastService: BroadcastService,
    private permissionsFacade: PermissionsFacade,
    private destroy$: DestroyService,
    private teamsActions: TeamsActions,
    private accountService: AccountService,
    private commentsFacade: CommentsFacade,
    private cdRef: ChangeDetectorRef,
    private hotkeysService: HotkeysService,
    @Inject(UI_MODAL_DATA) modalData: UpdateMetricModalData
  ) {
    Object.assign(this, modalData);

    this.permissionsFacade
      .hasPermission$("ManageGoals")
      .pipe(first())
      .subscribe((hasPermissionManageGoals) => {
        this.hasPermissionManageGoals = hasPermissionManageGoals;
      });

    this.datepickerOptions = {
      minDate: new Date(),
      maxDate: new Date(),
    };
    this.shouldDisableUpdateDateBound = this.shouldDisableUpdateDate.bind(this);

    this.initUpdateMetricForm();
  }

  public ngOnInit(): void {
    this.userId = getCurrentUserId();
    this.filterBySessionIdBound = this.filterBySessionId.bind(this);

    this.isCurrentUserAdmin = this.currentUserService.userHasRole("admin");

    const confidenceSettings = this.confidenceSettingsService.getConfidenceSettings();
    this.confidenceType = confidenceSettings?.confidenceType ?? "numeric";
    this.confidenceScale = this.confidenceSettingsService.getConfidenceScale();

    const { reduxStore } = reduxStoreContainer;
    const reduxObserver = new ReduxStoreObserver(reduxStore);

    reduxStore.dispatch(this.teamsActions.fetchTeamsIfMissing());
    reduxObserver
      .whenFetched$<IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState>("assignees", "sessions", "teams")
      .pipe(takeUntil(this.destroy$))
      .subscribe((storeData) => {
        this.sessions = storeData.sessions.items;
        this.assignees = storeData.assignees.map;
        this.teams = storeData.teams.map;

        this.getMetric(this.metricId);
      });
  }

  public ngOnDestroy(): void {
    this.hotkeysService.remove(this.maxDateHotkey);
  }

  public updateNoteValidator(control: AbstractControl): ValidationErrors | null {
    if (this.metric && (this.metric.insightName !== "" || this.metric.sourceMetricId) && !control.value?.note) {
      return {
        note: {
          value: control.value?.note,
        },
      };
    }

    return null;
  }

  public isActionAllowed(action: UserAction): boolean {
    return this.metric?.currentUserAllowedActions?.indexOf(action) > -1;
  }

  public isUpdateAllowed(): boolean {
    return this.hasPermissionManageGoals && !this.updateMetricForm.invalid && this.validationStates.isDataValid && this.isActionAllowed("update");
  }

  public save(): Promise<unknown> {
    if (this.updateMetricForm.invalid || !this.validationStates.isDataValid) {
      return Promise.resolve(false);
    }

    this.indicators.saving = { progress: true };

    const checkin: ICheckin = {
      checkInDate: this.updateMetricForm.value.dateOfUpdate.toISOString(),
      actual: this.updateMetricForm.value.newValue,
      confidence: this.updateMetricForm.value.confidence,
      comment: this.updateMetricForm.value.note.note,
      gif: this.updateMetricForm.value.note.gif,
      customFields: this.updateMetricForm.value.customFields,
    };

    this.cdRef.detectChanges();

    const save$ = this.metricsFacade
      .checkin$({ id: this.metric.id }, checkin, {
        metricUpdateNote: checkin.comment ? "yes" : "no",
        metricUpdateNoteMention: this.updateMetricForm.value.note?.mentions.nonRemovable.length ? "yes" : "no",
        update_session_cadence: this.planningSession.cadence?.type,
      })
      .pipe(switchMap((newCheckin) => this.postComment(newCheckin).pipe(map(() => newCheckin))));

    return firstValueFrom(save$).then(
      (newCheckin) => {
        delete this.indicators.saving;

        this.broadcastService.emit("goalsChanged");
        this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
          reason: "goalChanged",
        });
        this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
          reason: "metricUpdated",
          detail: {
            checkin: newCheckin,
            metric: this.metric,
            goal: this.goal,
            session: this.planningSession,
          },
        });
        this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
          reason: "metricSnapshotCreated",
          detail: {
            checkin: newCheckin,
            metric: this.metric,
            goal: this.goal,
            session: this.planningSession,
          },
        });

        return newCheckin;
      },
      (error) => {
        this.indicators.saving = { error };
        this.cdRef.detectChanges();
      }
    );
  }

  private postComment(checkin: ICheckin): Observable<unknown> {
    if (this.updateMetricForm.value.note?.note?.length) {
      const comment: NewComment = {
        mentioned: this.updateMetricForm.value.note.mentions.nonRemovable,
        targetId: checkin.id,
        targetType: "checkinMention",
        targetTitle: this.metric.name,
        text: checkin.comment,
        gif: checkin.gif,
      };

      return this.commentsFacade.postComment$(comment);
    }

    return of(null);
  }

  public shouldDisableUpdateDate(date: Date): boolean {
    if (!this.datepickerOptions || !date) {
      return false;
    }

    return shouldDisableMaxMinDate(date, this.datepickerOptions);
  }

  public filterBySessionId(customFields: ICustomField[]): ICustomField[] {
    const filteredCustomFields = filterCustomFieldsBySession(this.goal.sessionId, customFields);
    this.customFieldsLength = filteredCustomFields.length;
    return filteredCustomFields;
  }

  private areMultipleOwnersDisabled(settings: ISessionSettings): boolean {
    const sessionSetting = settings && settings.multipleOwners?.["disabledForMetrics"];
    const accountSetting = this.accountService.getAccountSetting<ISharedOkrsAccountSetting>("sharedOkrs")?.["krs"]?.disabled;

    return sessionSetting ?? accountSetting;
  }

  private initUpdateMetricForm(): void {
    this.updateMetricForm = this.formBuilder.group<UpdateMetricForm>({
      confidence: new FormControl(null),
      newValue: new FormControl(),
      customFields: new FormControl(),
      note: new FormControl(
        {
          note: null,
          gif: null,
          mentions: {
            removable: [],
            nonRemovable: [],
          },
        },
        [this.updateNoteValidator.bind(this)]
      ),
      dateOfUpdate: new FormControl(),
    });
    this.updateMetricForm.cacheKey = this.metricId;

    this.updateMetricForm.controls.note.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.removeAllMentionsAriaLabel = generateRemoveAllMentionsAriaLabel(value.mentions, this.assignees);
    });

    this.updateMetricForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdRef.detectChanges();
    });
  }

  private getMetric(metricId: string): void {
    this.metricsFacade.getMetric$(metricId).subscribe((metric) => {
      this.loading = false;

      if (!metric.confidence) {
        metric.confidence = defaultMetricConfidence;
      }

      this.metric = metric;

      const currentValue = new MetricPipe().transform(metric, "actual", { additionalInfo: "formatted" });
      const target = new MetricPipe().transform(metric, "targets", { additionalInfo: "update-metric" });

      this.statusAriaLabel = `${localize("current_value_cap")}, ${currentValue}, ${target}`;
      this.statusInfoAriaLabel = getMetricStatusInfoAriaLabel(this.metric);

      this.maxDateHotkey = this.hotkeysService.add(
        new Hotkey("shift+a", (): boolean => {
          this.datepickerOptions.maxDate = new Date("2038-04-03T08:58:37.791Z");
          return false; // Prevent bubbling
        })
      );

      this.addOwnersToMentioned();

      this.goal = {
        ...new Goal(),
        id: metric.goalId,
        sessionId: metric.sessionId,
      };

      this.planningSession = this.sessions.find((s) => s.id === this.goal.sessionId);

      const areMultipleKrOwnersDisabled = this.areMultipleOwnersDisabled(this.planningSession?.settings);
      this.ownerIds = getDisplayableOwnerIds(metric, "metric", { areMultipleKrOwnersDisabled, areMultipleObjectiveOwnersDisabled: undefined });

      this.adjustDates(this.planningSession);
      this.validate();

      this.updateMetricForm.controls.confidence.patchValue(this.metric.confidence.value);
      this.updateMetricForm.controls.newValue.patchValue(this.metric.actual);

      this.updateMetricForm.loadFromCache();

      this.cdRef.detectChanges();
    });
  }

  // Check and set different flags for alerts
  private validate(): void {
    const isDataValid = this.validateData();

    this.validationStates = {
      isDataValid,
      sessionStartsOnAlert: !this.validateData(this.datepickerOptions.minDate.toISOString(), this.currentDate),
      archivedMetricAlert: this.planningSession?.status === "closed",
      abandonedObjectiveAlert: this.metric.goalClosedStatus.status === "abandoned",
      krPastHardDeadlineAlert: !isDataValid && this.validateKrPastHardDeadline(),
      noSessionAlert: !this.goal.sessionId || this.goal.sessionId === "",
      hardDeadlineOutsideSession: !isDataValid && this.validateHardDeadlineOutsideSession(),
      deadlineAlert: this.metric.dueDate !== "0001-01-01T00:00:00Z",
      showRadioOptions:
        isDataValid && this.metric.manualType === "boolean" && this.metric.cascadeType === "" && this.metric.dynamic === false && !this.metric.sourceMetricId,
      showNumericValueField:
        isDataValid && this.metric.manualType !== "boolean" && this.metric.insightName === "" && !this.metric.cascadeType && !this.metric.sourceMetricId,
    };

    this.cdRef.detectChanges();
  }

  // we use this method to compare Dates and not strings
  private validateData(firstDate = null, secondDate = null, { strict } = { strict: true }, unit: dayjs.OpUnitType = "hours"): boolean {
    if (!this.metric) {
      return;
    }

    if (firstDate && secondDate) {
      const strictDifference = dayjs(new Date(secondDate)).diff(firstDate, "milliseconds");
      const notStrictDifference = dayjs(new Date(secondDate)).diff(firstDate, unit);
      return strict ? strictDifference >= 0 : notStrictDifference > 0;
    }

    if (sessionIsArchivedOrInTheFutureOrOkrIsAbandoned(this.planningSession, this.metric, this.datepickerOptions.minDate, this.currentDate, unit)) {
      return false;
    }

    if (
      (noDueDate(this.metric.dueDate) || dueDateIsInTheSessionTimePeriod(this.planningSession, this.metric, unit)) &&
      (this.isCurrentUserAdmin || noDueDateOrHardDateIsInTheFuture(this.metric.dueDate, this.currentDate, unit))
    ) {
      return true;
    }

    return false;
  }

  private validateKrPastHardDeadline(): boolean {
    return (
      this.planningSession?.status !== "closed" &&
      this.metric.goalClosedStatus.status !== "abandoned" &&
      this.validateData(this.planningSession?.start, this.currentDate) &&
      this.validateData(this.planningSession?.start, this.metric.dueDate) &&
      this.validateData(this.metric.dueDate, this.planningSession?.end)
    );
  }

  private validateHardDeadlineOutsideSession(): boolean {
    return (
      this.planningSession?.status !== "closed" &&
      this.metric.goalClosedStatus.status !== "abandoned" &&
      !this.validateData(this.currentDate, this.planningSession?.start) &&
      (this.validateData(this.metric.dueDate, this.planningSession?.start, { strict: false }, "milliseconds") ||
        this.validateData(this.planningSession?.end, this.metric.dueDate, { strict: false }, "milliseconds"))
    );
  }

  private adjustDates(session?: Session): void {
    this.currentDate = new Date().toISOString();
    let checkInDate = new Date();

    if (session) {
      this.datepickerOptions.minDate = new Date(session.start);
    }

    if (!noDueDate(this.metric.dueDate) && dayjs().diff(this.metric.dueDate, "days") > 0) {
      checkInDate = new Date(this.metric.dueDate);
      this.datepickerOptions.maxDate = new Date(this.metric.dueDate);
    }

    this.updateMetricForm.controls.dateOfUpdate.patchValue(checkInDate);
  }

  private addOwnersToMentioned(): void {
    const allUserOwnerIds = getOwnerIds(this.metric.ownerIds, this.assignees, this.teams);
    allUserOwnerIds.delete(this.userId);

    this.updateMetricForm.controls.note.patchValue({
      note: null,
      gif: null,
      mentions: {
        removable: Array.from(allUserOwnerIds),
        nonRemovable: [],
      },
    });
  }
}
